import type { CallOutcomeCode, QueuePeekItem, TelesalesScope } from '../api/types';
import { fmtDateTime, fmtTime, localDate, outcomeLabels, timeZone } from './format';

// Zeitpunkt kurz: heute nur die Uhrzeit, sonst Datum mit Uhrzeit.
export function shortDue(iso?: string | null): string {
  if (!iso) return '–';
  return localDate(new Date(iso)) === localDate() ? `${fmtTime(iso)} Uhr` : fmtDateTime(iso);
}

const dayMonth = new Intl.DateTimeFormat('de-DE', { timeZone, day: '2-digit', month: '2-digit' });

// listDue: für Listen – heute „09:30“, sonst „25.09. 18:21“.
export function listDue(iso: string): string {
  const d = new Date(iso);
  return localDate(d) === localDate() ? fmtTime(iso) : `${dayMonth.format(d)} ${fmtTime(iso)}`;
}

// taskNote: Notiz der Wiedervorlage ohne den automatisch vorangestellten Typ („Rückruf: …“).
export const taskNote = (title?: string | null) => (title ?? '').replace(/^(Rückruf|Wiedervorlage)(:\s*|$)/, '').trim();

// nextCallReason: warum der Lead als Nächstes dran ist (Reihenfolge der Akquise-Warteschlange).
export function nextCallReason(item: QueuePeekItem, now = new Date()): string {
  const overdue = item.due_at ? new Date(item.due_at) < now : false;
  const where = [item.area_name && `Gebiet ${item.area_name}`, item.field_sales_name && `Makler ${item.field_sales_name}`].filter(Boolean).join(' · ');
  switch (item.tier) {
    case 'CALLBACK_DUE':
      return `Rückruf vereinbart für ${shortDue(item.due_at)}${overdue ? ' – überfällig' : ''}`;
    case 'DIALFIRE_APPOINTMENT':
      return where ? `Frischer Termin aus Dialfire · ${where}` : 'Frischer Termin aus Dialfire – zuerst anrufen';
    case 'FOLLOW_UP_DUE':
      return `Wiedervorlage fällig seit ${shortDue(item.due_at)}`;
    case 'FOLLOW_UP_TODAY':
      return `Wiedervorlage heute um ${fmtTime(item.due_at)} Uhr – darf vorgezogen werden`;
    case 'NEW_PRIORITIZED':
      return 'Neuer Lead mit Priorität';
    case 'NEW_FIELD_SALES':
      return where ? `Neuer Lead · ${where}` : 'Neuer Lead in einem Gebiet mit Makler';
    case 'RETRY_FIELD_SALES':
      return where ? `Erneuter Versuch · ${where}` : 'Erneuter Versuch in einem Gebiet mit Makler';
    case 'NEW':
      return 'Neuer Lead';
    default:
      return 'Erneuter Versuch';
  }
}

// attemptsText: bisherige Kontaktversuche kurz.
export function attemptsText(item: QueuePeekItem): string {
  if (item.contact_attempts === 0) return 'Noch nie angerufen';
  const outcome = item.last_call_outcome ? outcomeLabels[item.last_call_outcome as CallOutcomeCode] ?? item.last_call_outcome : '';
  return `${item.contact_attempts} ${item.contact_attempts === 1 ? 'Versuch' : 'Versuche'} bisher · zuletzt ${fmtDateTime(item.last_call_at)}${outcome ? ` (${outcome})` : ''}`;
}

export const placeText = (item: QueuePeekItem) => [item.postal_code, item.city].filter(Boolean).join(' ');

// scopeText: „Manfred Eisenhardt · Gebiet Stuttgart Mitte“ bzw. „alle Leads“ (Zuständigkeit eines Telefonisten).
export function scopeText(scope: TelesalesScope | { field_sales: string[]; areas: string[] }): string {
  const name = (x: string | { name: string }) => (typeof x === 'string' ? x : x.name);
  const parts = [...scope.field_sales.map(name), ...scope.areas.map((a) => `Gebiet ${name(a)}`)];
  return parts.length ? parts.join(' · ') : 'alle Leads';
}
